"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { DetectionResult, DetectorCommand } from "@/lib/navigation/vision/types";

export function useVisionDetector() {
    const worker = useRef<Worker | null>(null);
    const counter = useRef(0);
    const mounted = useRef(true);
    const pending = useRef(new Map<number, { resolve: (value: unknown) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> }>());
    const loading = useRef<Promise<void> | null>(null);
    const initialized = useRef(false);
    const queue = useRef<Promise<unknown>>(Promise.resolve());
    const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
    const [backend, setBackend] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        mounted.current = true;
        const requests = pending.current;
        return () => {
            mounted.current = false; worker.current?.terminate(); worker.current = null; initialized.current = false;
            for (const request of requests.values()) { clearTimeout(request.timer); request.reject(new Error("Детектор закрито.")); }
            requests.clear();
        };
    }, []);

    const send = useCallback(<T,>(command: DetectorCommand, transfer: Transferable[] = []): Promise<T> => new Promise((resolve, reject) => {
        if (!mounted.current) { reject(new Error("Детектор закрито.")); return; }
        if (!worker.current) {
            const instance = new Worker(new URL("../../../lib/navigation/vision/detector.worker.ts", import.meta.url));
            worker.current = instance;
            instance.onmessage = ({ data }: MessageEvent<{ requestId: number; ok: boolean; result?: unknown; error?: string }>) => {
                const request = pending.current.get(data.requestId);
                if (!request) return;
                clearTimeout(request.timer); pending.current.delete(data.requestId);
                if (data.ok) request.resolve(data.result); else request.reject(new Error(data.error ?? "Помилка детектора."));
            };
            instance.onerror = () => {
                const failure = new Error("Обчислювальний модуль детектора недоступний. Спробуйте запустити його знову.");
                initialized.current = false; loading.current = null;
                for (const request of pending.current.values()) { clearTimeout(request.timer); request.reject(failure); }
                pending.current.clear(); instance.terminate(); worker.current = null;
                if (mounted.current) { setStatus("error"); setError(failure.message); }
            };
        }
        const requestId = ++counter.current;
        const timer = setTimeout(() => {
            const failure = new Error("Детектор не відповів вчасно. Спробуйте ще раз.");
            for (const request of pending.current.values()) { clearTimeout(request.timer); request.reject(failure); }
            pending.current.clear(); worker.current?.terminate(); worker.current = null; initialized.current = false;
            if (mounted.current) { setStatus("error"); setError(failure.message); }
        }, command.type === "init" ? 90000 : 30000);
        pending.current.set(requestId, { resolve: value => resolve(value as T), reject, timer });
        worker.current.postMessage({ ...command, requestId }, transfer);
    }), []);

    const initialize = useCallback((): Promise<void> => {
        if (initialized.current) return Promise.resolve();
        if (loading.current) return loading.current;
        setStatus("loading"); setError("");
        loading.current = send<{ backend: string }>({ type: "init", origin: window.location.origin }).then(result => {
            initialized.current = true;
            if (mounted.current) { setBackend(result.backend); setStatus("ready"); }
        }).catch(failure => {
            worker.current?.terminate(); worker.current = null; initialized.current = false;
            const message = "Не вдалося завантажити модель. Перевірте інтернет і повторіть запуск.";
            if (mounted.current) { setStatus("error"); setError(message); }
            throw new Error(message, { cause: failure });
        }).finally(() => { loading.current = null; });
        return loading.current;
    }, [send]);

    const detect = useCallback((frame: { pixels: ArrayBuffer; width: number; height: number }, threshold: number, signal: AbortSignal): Promise<DetectionResult> => {
        const operation = queue.current.catch(() => {}).then(async () => {
            if (signal.aborted) throw new DOMException("Frame cancelled", "AbortError");
            await initialize();
            if (signal.aborted) throw new DOMException("Frame cancelled", "AbortError");
            return send<DetectionResult>({ type: "detect", ...frame, threshold }, [frame.pixels]);
        });
        queue.current = operation;
        return operation;
    }, [initialize, send]);

    return { status, backend, error, initialize, detect };
}
